import { Combobox as BaseCombobox, type ComboboxListProps, type ComboboxPopupProps, type ComboboxPositionerProps } from '@base-ui/react';
import { assignInlineVars } from '@vanilla-extract/dynamic';
import { useTranslation } from 'react-i18next';

import { useCollisionPadding } from '~/hooks/useCollisionPadding';
import { clsxs } from '~/styles/utils';

import { emptyStyle, listStyle, popupMinWidth, popupStyle, positionerStyle } from '../form.css';

type Props = Omit<ComboboxPopupProps, 'children'> &
  Pick<ComboboxListProps, 'children'> & {
    align?: ComboboxPositionerProps['align'];
    emptyMessage?: string;
    minWidth?: string;
  };

export const ComboboxList = ({ align = 'center', children, emptyMessage, minWidth, ...delegated }: Props) => {
  const collisionPadding = useCollisionPadding();

  const { t } = useTranslation(['common']);

  return (
    <BaseCombobox.Portal>
      <BaseCombobox.Positioner align={align} className={positionerStyle} collisionPadding={collisionPadding} sideOffset={4}>
        <BaseCombobox.Popup
          {...delegated}
          className={clsxs(popupStyle, delegated.className)}
          style={(state) => ({
            ...(minWidth && assignInlineVars({ [popupMinWidth]: `${minWidth}` })),
            ...(typeof delegated.style === 'function' ? delegated.style(state) : delegated.style),
          })}
        >
          <BaseCombobox.List className={listStyle}>{children}</BaseCombobox.List>

          <BaseCombobox.Empty>
            <div className={emptyStyle}>{emptyMessage ?? t('common:listbox.noOptions')}</div>
          </BaseCombobox.Empty>
        </BaseCombobox.Popup>
      </BaseCombobox.Positioner>
    </BaseCombobox.Portal>
  );
};
